import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import {
  InvoiceDetailsLineType,
  ThirdPartyInvoiceDetail,
  ThirdPartyInvoiceHeader,
} from '../../api/models/third-party-invoice.models';
import { filterObjects, getSortComparer } from '../../helpers';
import { RootState } from '../store';

const invoiceHeadersAdapter = createEntityAdapter<ThirdPartyInvoiceHeader>({
  selectId: (header) => header.ThirdPartyInvoiceHeaderId,
  sortComparer: (a, b) => a.InvoiceNumber.localeCompare(b.InvoiceNumber),
});

const invoiceDetailsAdapter = createEntityAdapter<ThirdPartyInvoiceDetail>({
  selectId: (detail) => detail.ThirdPartyInvoiceDetailId,
  sortComparer: getSortComparer((detail) => detail.CreateDate),
});

interface ThirdPartyInvoiceState {
  invoiceHeaders: EntityState<ThirdPartyInvoiceHeader>;
  isInvoiceHeadersLoading: boolean;
  invoiceDetails: EntityState<ThirdPartyInvoiceDetail>;
  isInvoiceDetailsLoading: boolean;
}

const initialState: ThirdPartyInvoiceState = {
  invoiceHeaders: invoiceHeadersAdapter.getInitialState(),
  isInvoiceHeadersLoading: false,
  invoiceDetails: invoiceDetailsAdapter.getInitialState(),
  isInvoiceDetailsLoading: false,
};

export const thirdPartyInvoiceSlice = createSlice({
  name: 'thirdPartyInvoice',
  initialState: initialState,
  reducers: {
    resetState: () => initialState,
    setInvoiceHeaders: (state: ThirdPartyInvoiceState, action: PayloadAction<ThirdPartyInvoiceHeader[]>) => {
      invoiceHeadersAdapter.setAll(state.invoiceHeaders, action.payload);
    },
    setInvoiceHeader: (state: ThirdPartyInvoiceState, action: PayloadAction<ThirdPartyInvoiceHeader>) => {
      invoiceHeadersAdapter.setOne(state.invoiceHeaders, action.payload);
    },
    setIsInvoiceHeadersLoading: (state: ThirdPartyInvoiceState, action: PayloadAction<boolean>) => {
      state.isInvoiceHeadersLoading = action.payload;
    },
    removeInvoiceHeader: (state: ThirdPartyInvoiceState, action: PayloadAction<string>) => {
      invoiceHeadersAdapter.removeOne(state.invoiceHeaders, action.payload);
    },
    setInvoiceDetails: (state: ThirdPartyInvoiceState, action: PayloadAction<ThirdPartyInvoiceDetail[]>) => {
      invoiceDetailsAdapter.setAll(state.invoiceDetails, action.payload);
    },
    updateInvoiceDetails: (state: ThirdPartyInvoiceState, action: PayloadAction<ThirdPartyInvoiceDetail[]>) => {
      invoiceDetailsAdapter.setMany(state.invoiceDetails, action.payload);
    },
    removeInvoiceDetail: (state: ThirdPartyInvoiceState, action: PayloadAction<string>) => {
      invoiceDetailsAdapter.removeOne(state.invoiceDetails, action.payload);
    },
    setIsInvoiceDetailsLoading: (state: ThirdPartyInvoiceState, action: PayloadAction<boolean>) => {
      state.isInvoiceDetailsLoading = action.payload;
    },
  },
});

export const {
  selectById: selectThirdPartyInvoiceHeaderById,
  selectAll: selectAllThirdPartyInvoiceHeaders,
  selectIds: selectAllThirdPartyInvoiceHeaderIds,
} = invoiceHeadersAdapter.getSelectors((s: RootState) => s.thirdPartyInvoice.invoiceHeaders);

export const {
  selectById: selectThirdPartyInvoiceDetailById,
  selectAll: selectAllThirdPartyInvoiceDetails,
  selectIds: selectAllThirdPartyInvoiceDetailIds,
} = invoiceDetailsAdapter.getSelectors((s: RootState) => s.thirdPartyInvoice.invoiceDetails);

export const selectThirdPartyInvoicesGridData = createSelector(
  [selectAllThirdPartyInvoiceHeaders, (state: RootState, filterText: string) => filterText],
  (invoiceHeaders, filterText) => {
    if (!filterText) {
      return invoiceHeaders.map((header) => header.ThirdPartyInvoiceHeaderId);
    }

    const objects = filterObjects(
      invoiceHeaders,
      filterText,
      ['InvoiceNumber', 'PONumber', 'Comment'],
      [
        {
          field: 'InvoiceNumber',
          type: 'custom',
          filterFunction: (val: string, queryString: string) =>
            val.toLocaleLowerCase().includes(queryString.toLocaleLowerCase()),
        },
        {
          field: 'PONumber',
          type: 'custom',
          filterFunction: (val: string, queryString: string) =>
            val.toLocaleLowerCase().includes(queryString.toLocaleLowerCase()),
        },
        {
          field: 'Comment',
          type: 'custom',
          filterFunction: (val: string, queryString: string) =>
            val.toLocaleLowerCase().includes(queryString.toLocaleLowerCase()),
        },
      ]
    );

    return objects.map((h) => h.ThirdPartyInvoiceHeaderId);
  }
);

export const selectThirdPartyInvoiceDetailsGridData = createSelector(
  [selectAllThirdPartyInvoiceDetails, (state: RootState, filterText: string) => filterText],
  (invoiceDetails, filterText) => {
    if (!filterText) {
      return invoiceDetails.map((header) => header.ThirdPartyInvoiceDetailId);
    }

    const objects = filterObjects(
      invoiceDetails,
      filterText,
      ['ProductDescription', 'LineType', 'ThirdPartyInvoiceDetailProductUnitOfMeasures'],
      [
        {
          field: 'ProductDescription',
          type: 'custom',
          filterFunction: (val: string, queryString: string) =>
            val.toLocaleLowerCase().includes(queryString.toLocaleLowerCase()),
        },
        {
          field: 'LineType',
          type: 'custom',
          filterFunction: (val: string, queryString: string) => {
            const lineTypeNum = Number.parseInt(val);
            const enumVal = InvoiceDetailsLineType[lineTypeNum];
            return enumVal.toLocaleLowerCase().includes(queryString.toLocaleLowerCase());
          },
        },
        {
          field: 'ThirdPartyInvoiceDetailProductUnitOfMeasures',
          type: 'custom',
          filterFunction: (val: string, queryString: string, obj: ThirdPartyInvoiceDetail) => {
            const productNumber = obj.ThirdPartyInvoiceDetailProductUnitOfMeasures[0]?.ProductNumber;

            return productNumber?.toLocaleLowerCase().includes(queryString.toLocaleLowerCase());
          },
        },
      ]
    );

    return objects.map((h) => h.ThirdPartyInvoiceDetailId);
  }
);
